import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  Alert,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { enqueueSnackbar } from "notistack";
import { useApiClient } from "../../../api";
import { FruitifyContainer } from "../../../shared-components/Containers";
import EmailContentEditorDialog from "../onboarding/EmailContentEditorDialog";
import SegmentPicker from "./SegmentPicker";
import {
  scheduleTimezone,
  localScheduleInput,
  scheduleTimestamp,
  nextAnnouncementPickup,
  displaySchedule,
} from "./announcementSchedule";
import { act, read, localId } from "./api";

const EMPTY_DRAFT = {
  name: "",
  subject: "",
  bodyText: "",
  senderId: "",
  batchSize: 50,
  useBrandedLayout: true,
};

export default function AnnouncementsPage() {
  const api = useApiClient().marketing;
  const { announcementId } = useParams();
  const navigate = useNavigate();
  const [items, setItems] = useState({ items: [], total: 0 });
  const [draft, setDraft] = useState({
    name: "",
    subject: "",
    bodyText: "",
    senderId: "",
    batchSize: 50,
    useBrandedLayout: true,
  });
  const [scheduleMode, setScheduleMode] = useState("now");
  const [scheduledInput, setScheduledInput] = useState("");
  const [segments, setSegments] = useState([]);
  const [users, setUsers] = useState([]);
  const [schools, setSchools] = useState([]);
  const [senders, setSenders] = useState([]);
  const [editing, setEditing] = useState(false);
  const [review, setReview] = useState(null);
  const [reviewPage, setReviewPage] = useState(1);
  const [confirm, setConfirm] = useState(false);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");
  const [recover, setRecover] = useState(null);
  const [reason, setReason] = useState("");
  const isNew = announcementId === "new";
  const isDraft = isNew || draft.status === "draft";
  const load = useCallback(async () => {
    try {
      const management = await api.getManagement();
      setSenders(management.marketingSenders || []);
      if (announcementId && !isNew) {
        const row = await read(api, "announcement", {
          id: Number(announcementId),
        });
        setDraft(row);
        setScheduleMode(row.scheduledFor ? "later" : "now");
        setScheduledInput(localScheduleInput(row.scheduledFor));
        setUsers(row.audienceUsers || []);
        setSchools(row.audienceSchools || []);
        setSegments(
          await Promise.all(
            (row.audience.segment_ids || []).map((id) =>
              read(api, "segment", { id }),
            ),
          ),
        );
        setItems(
          await read(api, "deliveries", {
            id: row.id,
            search,
            page,
            kind: status || undefined,
          }),
        );
      } else if (!announcementId)
        setItems(await read(api, "announcements", { search, page }));
      setError("");
    } catch (err) {
      setError(err.message);
    }
  }, [api, announcementId, isNew, search, page, status]);
  useEffect(() => {
    load();
  }, [load]);
  useEffect(() => {
    setReview(null);
    setReviewPage(1);
    setPage(1);
    if (isNew) {
      setDraft(EMPTY_DRAFT);
      setScheduleMode("now");
      setScheduledInput("");
      setUsers([]);
      setSchools([]);
      setSegments([]);
    }
  }, [announcementId, isNew]);
  const mutate = async (action, data) => {
    setBusy(true);
    try {
      return await act(api, action, data);
    } catch (err) {
      enqueueSnackbar(err.message, { variant: "error" });
      return null;
    } finally {
      setBusy(false);
    }
  };
  const save = async () => {
    let scheduledFor = null;
    try {
      if (scheduleMode === "later")
        scheduledFor = scheduleTimestamp(scheduledInput);
    } catch (error) {
      enqueueSnackbar(error.message, { variant: "error" });
      return null;
    }
    const saved = await mutate("saveAnnouncement", {
      ...draft,
      scheduledFor,
      id: isNew ? undefined : draft.id,
      audience: {
        segment_ids: segments.map((row) => row.id),
        user_ids: users.map((row) => row.id),
        school_ids: schools.map((row) => row.id),
      },
    });
    if (saved) {
      setDraft(saved);
      setReview(null);
      enqueueSnackbar("Announcement saved.", { variant: "success" });
      if (isNew) navigate(`/superuser/marketing/announcements/${saved.id}`);
    }
    return saved;
  };
  const update = (values) => {
    setDraft((old) => ({ ...old, ...values }));
    setReview(null);
  };
  const selectedSender = senders.find(
    (sender) => localId(sender.id) === Number(draft.senderId),
  );
  return (
    <FruitifyContainer>
      <Stack spacing={3}>
        <Typography variant="h4">
          {announcementId ? "Announcement" : "Announcements"}
        </Typography>
        {error && <Alert severity="error">{error}</Alert>}
        {!announcementId ? (
          <>
            <Button
              variant="contained"
              component={Link}
              to="/superuser/marketing/announcements/new"
            >
              Create announcement
            </Button>
            <TextField
              label="Search announcements"
              value={search}
              onChange={(event) => {
                setPage(1);
                setSearch(event.target.value);
              }}
            />
            {items.items.map((row) => (
              <Paper key={row.id} sx={{ p: 2 }}>
                <Typography
                  component={Link}
                  to={`/superuser/marketing/announcements/${row.id}`}
                >
                  {row.name}
                </Typography>
                <Typography>{row.status}</Typography>
              </Paper>
            ))}
          </>
        ) : (
          <>
            <Button component={Link} to="/superuser/marketing/announcements">
              All announcements
            </Button>
            <Typography>
              BCC batches keep addresses private. Sending starts without
              workflow pacing or configured daily sender limits. Everyone
              receives the same content and an email-confirmation unsubscribe
              link.
            </Typography>
            <Typography>
              {draft.status || "draft"}
              {draft.diagnostic ? ` · ${draft.diagnostic}` : ""}
            </Typography>
            {isDraft ? (
              <Paper sx={{ p: 2 }}>
                <Stack spacing={2}>
                  <TextField
                    label="Internal name"
                    value={draft.name || ""}
                    onChange={(event) => update({ name: event.target.value })}
                  />
                  <TextField
                    select
                    label="Sender"
                    value={draft.senderId || ""}
                    onChange={(event) =>
                      update({ senderId: event.target.value })
                    }
                  >
                    {senders.map((row) => (
                      <MenuItem key={row.id} value={localId(row.id)}>
                        {row.displayName} — {row.fromEmail}
                      </MenuItem>
                    ))}
                  </TextField>
                  <TextField
                    label="Recipients per BCC batch"
                    type="number"
                    inputProps={{ min: 1, max: 100 }}
                    value={draft.batchSize}
                    onChange={(event) =>
                      update({ batchSize: Number(event.target.value) })
                    }
                  />
                  <TextField
                    label="Subject"
                    value={draft.subject || ""}
                    onChange={(event) =>
                      update({ subject: event.target.value })
                    }
                  />
                  <TextField
                    label="Plain text message"
                    multiline
                    minRows={4}
                    disabled={!!draft.contentDocument?.content}
                    value={draft.bodyText || ""}
                    onChange={(event) =>
                      update({ bodyText: event.target.value })
                    }
                  />
                  <Button onClick={() => setEditing(true)}>
                    Edit formatted email
                  </Button>
                  <SegmentPicker
                    api={api}
                    value={segments}
                    onChange={(value) => {
                      setSegments(value);
                      setReview(null);
                    }}
                  />
                  <SegmentPicker
                    api={api}
                    entity="audience_users"
                    label="Additional teachers"
                    value={users}
                    onChange={(value) => {
                      setUsers(value);
                      setReview(null);
                    }}
                  />
                  <SegmentPicker
                    api={api}
                    entity="audience_schools"
                    label="Additional schools"
                    value={schools}
                    onChange={(value) => {
                      setSchools(value);
                      setReview(null);
                    }}
                  />
                  <Typography variant="body2">
                    All teachers does not include outreach contacts or students.
                    Select outreach-contact segments explicitly. Ineligible
                    members are skipped.
                  </Typography>
                  <Paper
                    component="section"
                    variant="outlined"
                    aria-labelledby="announcement-scheduling-heading"
                    sx={{ p: 2 }}
                  >
                    <Stack spacing={2}>
                      <Typography
                        id="announcement-scheduling-heading"
                        variant="h6"
                      >
                        Scheduling
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Choose when to start sending this announcement.
                      </Typography>
                      <TextField
                        select
                        label="Send time"
                        value={scheduleMode}
                        onChange={(event) => {
                          setScheduleMode(event.target.value);
                          setReview(null);
                        }}
                      >
                        <MenuItem value="now">Send now</MenuItem>
                        <MenuItem value="later">Schedule for later</MenuItem>
                      </TextField>
                      {scheduleMode === "later" && (
                        <>
                          <TextField
                            type="datetime-local"
                            label={`Send no earlier than (${scheduleTimezone})`}
                            value={scheduledInput}
                            InputLabelProps={{ shrink: true }}
                            onChange={(event) => {
                              setScheduledInput(event.target.value);
                              setReview(null);
                            }}
                          />
                          <Typography variant="body2">
                            Scheduled announcements are picked up by the next
                            :15/:45 UTC check, then sent automatically in
                            batches. Times use {scheduleTimezone}.
                          </Typography>
                          {scheduledInput &&
                            Number.isFinite(
                              new Date(scheduledInput).getTime(),
                            ) && (
                              <Typography variant="body2">
                                Expected pickup:{" "}
                                {displaySchedule(
                                  nextAnnouncementPickup(scheduledInput),
                                )}{" "}
                                ({scheduleTimezone}). Worker availability and
                                provider restrictions may delay delivery.
                              </Typography>
                            )}
                        </>
                      )}
                    </Stack>
                  </Paper>
                  <Stack direction="row" flexWrap="wrap" gap={1}>
                    <Button
                      disabled={busy || !draft.name || !draft.senderId}
                      onClick={save}
                    >
                      Save draft
                    </Button>
                    <Button
                      disabled={busy || !draft.id}
                      onClick={async () => {
                        const saved = await save();
                        setReviewPage(1);
                        if (saved) {
                          const result = await mutate("testAnnouncement", {
                            id: saved.id,
                          });
                          if (result)
                            enqueueSnackbar(result.message, {
                              variant: "success",
                            });
                        }
                      }}
                    >
                      Send test to me
                    </Button>
                    <Button
                      disabled={busy || !draft.senderId}
                      onClick={async () => {
                        const saved = await save();
                        setReviewPage(1);
                        if (saved)
                          setReview(
                            await mutate("previewAnnouncement", {
                              id: saved.id,
                            }),
                          );
                      }}
                    >
                      Preview audience and email
                    </Button>
                  </Stack>
                  {review && (
                    <Dialog
                      open={!confirm}
                      onClose={() => !busy && setReview(null)}
                      fullWidth
                      maxWidth="md"
                    >
                      <DialogTitle>
                        Preview announcement email and audience
                      </DialogTitle>
                      <DialogContent dividers>
                        <Stack spacing={2}>
                          {draft.scheduledFor && (
                            <ScheduleDetails value={draft.scheduledFor} />
                          )}
                          <Typography>
                            {review.eligible} eligible of {review.selected}{" "}
                            selected.{" "}
                            {Object.entries(review.exclusions)
                              .map(([key, count]) => `${key}: ${count}`)
                              .join("; ")}
                          </Typography>
                          <Paper
                            component="iframe"
                            title="Announcement preview"
                            sandbox=""
                            srcDoc={
                              review.content.body_html ||
                              `<pre>${review.content.body_text.replaceAll("&", "&amp;").replaceAll("<", "&lt;")}</pre>`
                            }
                            sx={{ width: "100%", minHeight: 350, border: 0 }}
                          />
                          {review.recipients.map((row) => (
                            <Typography key={`${row.kind}-${row.id}`}>
                              {row.email}: {row.reason || "eligible"}
                            </Typography>
                          ))}
                          <Stack direction="row" spacing={2}>
                            <Button
                              disabled={busy || reviewPage === 1}
                              onClick={async () => {
                                const result = await mutate(
                                  "previewAnnouncement",
                                  {
                                    id: draft.id,
                                    page: reviewPage - 1,
                                  },
                                );
                                if (result) {
                                  setReview(result);
                                  setReviewPage(reviewPage - 1);
                                }
                              }}
                            >
                              Previous recipients
                            </Button>
                            <Typography>Page {reviewPage}</Typography>
                            <Button
                              disabled={
                                busy || reviewPage * 25 >= review.selected
                              }
                              onClick={async () => {
                                const result = await mutate(
                                  "previewAnnouncement",
                                  {
                                    id: draft.id,
                                    page: reviewPage + 1,
                                  },
                                );
                                if (result) {
                                  setReview(result);
                                  setReviewPage(reviewPage + 1);
                                }
                              }}
                            >
                              Next recipients
                            </Button>
                          </Stack>
                        </Stack>
                      </DialogContent>
                      <DialogActions>
                        <Button disabled={busy} onClick={() => setReview(null)}>
                          Close preview
                        </Button>
                        <Button
                          variant="contained"
                          disabled={busy || !review.eligible}
                          onClick={() => setConfirm(true)}
                        >
                          {draft.scheduledFor
                            ? "Schedule announcement"
                            : "Send announcement"}
                        </Button>
                      </DialogActions>
                    </Dialog>
                  )}
                </Stack>
              </Paper>
            ) : (
              <>
                {draft.scheduledFor && (
                  <ScheduleDetails value={draft.scheduledFor} />
                )}
                <Typography>
                  {Object.entries(draft.counts || {})
                    .map(([key, count]) => `${key}: ${count}`)
                    .join(" · ")}
                </Typography>
                <Stack direction="row" gap={1}>
                  <Button onClick={load}>Refresh delivery status</Button>
                  <Button
                    disabled={busy}
                    onClick={async () => {
                      const row = await mutate("cloneAnnouncement", {
                        id: draft.id,
                      });
                      if (row)
                        navigate(
                          `/superuser/marketing/announcements/${row.id}`,
                        );
                    }}
                  >
                    Clone to draft
                  </Button>
                  {["scheduled", "queued", "sending"].includes(
                    draft.status,
                  ) && (
                    <Button
                      color="error"
                      disabled={busy}
                      onClick={async () => {
                        if (
                          window.confirm(
                            "Cancel remaining unsent emails? Sent emails cannot be recalled.",
                          )
                        ) {
                          await mutate("cancelAnnouncement", { id: draft.id });
                          await load();
                        }
                      }}
                    >
                      Cancel remaining emails
                    </Button>
                  )}
                </Stack>
                <TextField
                  label="Search email"
                  value={search}
                  onChange={(event) => {
                    setSearch(event.target.value);
                    setPage(1);
                  }}
                />
                <TextField
                  select
                  label="Delivery status"
                  value={status}
                  onChange={(event) => {
                    setStatus(event.target.value);
                    setPage(1);
                  }}
                >
                  {[
                    "",
                    "pending",
                    "queued",
                    "sending",
                    "sent",
                    "failed",
                    "skipped",
                  ].map((value) => (
                    <MenuItem key={value} value={value}>
                      {value || "All"}
                    </MenuItem>
                  ))}
                </TextField>
                {items.items.map((row) => (
                  <Paper key={row.id} sx={{ p: 2 }}>
                    <Typography>
                      {row.email}: {row.status} · {row.attempts} attempts{" "}
                      {row.diagnostic}
                    </Typography>
                    {row.diagnostic === "unknown_outcome" && (
                      <Button
                        onClick={() => {
                          setRecover(row);
                          setReason("");
                        }}
                      >
                        Confirm not sent and retry
                      </Button>
                    )}
                  </Paper>
                ))}
              </>
            )}
            {editing && (
              <EmailContentEditorDialog
                open
                email={draft}
                sender={selectedSender}
                allowedFields={[]}
                onClose={() => setEditing(false)}
                onApply={(email) => {
                  update(email);
                  setEditing(false);
                }}
              />
            )}
          </>
        )}
        {(!announcementId || !isDraft) && (
          <Stack direction="row" gap={2}>
            <Button disabled={page <= 1} onClick={() => setPage(page - 1)}>
              Previous
            </Button>
            <Typography>
              Page {page} · {items.total} results
            </Typography>
            <Button
              disabled={page * 25 >= items.total}
              onClick={() => setPage(page + 1)}
            >
              Next
            </Button>
          </Stack>
        )}
        <Dialog open={confirm} onClose={() => !busy && setConfirm(false)}>
          <DialogTitle>Confirm announcement</DialogTitle>
          <DialogContent>
            {draft.scheduledFor && (
              <ScheduleDetails value={draft.scheduledFor} />
            )}
            <Typography>
              Send {draft.name} from {selectedSender?.fromEmail} to{" "}
              {review?.eligible} recipients in BCC batches of up to{" "}
              {draft.batchSize}? Content and audience will be frozen.{" "}
              {draft.scheduledFor
                ? "Sending starts automatically when the schedule is picked up."
                : "Sending starts automatically in the background."}{" "}
              Batches continue automatically, subject to provider availability.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button disabled={busy} onClick={() => setConfirm(false)}>
              Back
            </Button>
            <Button
              disabled={busy}
              onClick={async () => {
                const row = await mutate("launchAnnouncement", {
                  id: draft.id,
                  revision: review.revision,
                });
                setConfirm(false);
                if (row) {
                  setReview(null);
                  enqueueSnackbar(
                    row.scheduledFor
                      ? "Announcement scheduled. Sending will start automatically at the next eligible pickup."
                      : "Announcement started. Emails will send automatically.",
                    {
                      variant: "success",
                    },
                  );
                  await load();
                } else setReview(null);
              }}
            >
              {draft.scheduledFor
                ? "Confirm schedule"
                : "Send announcement now"}
            </Button>
          </DialogActions>
        </Dialog>
        <Dialog open={!!recover} onClose={() => setRecover(null)}>
          <DialogTitle>Confirm this email was not sent</DialogTitle>
          <DialogContent>
            <Typography>
              Check the mailbox/provider records before retrying an uncertain
              delivery. Retrying can send a duplicate.
            </Typography>
            <TextField
              fullWidth
              label="Evidence / reason"
              value={reason}
              onChange={(event) => setReason(event.target.value)}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setRecover(null)}>Cancel</Button>
            <Button
              disabled={busy || !reason.trim()}
              onClick={async () => {
                if (
                  await mutate("recoverAnnouncement", {
                    id: recover.id,
                    reason,
                    confirmedNotSent: true,
                  })
                ) {
                  setRecover(null);
                  enqueueSnackbar("Retry queued.", { variant: "success" });
                  load();
                }
              }}
            >
              Confirm not sent and retry
            </Button>
          </DialogActions>
        </Dialog>
      </Stack>
    </FruitifyContainer>
  );
}

function ScheduleDetails({ value }) {
  return (
    <Stack spacing={0.5} mb={2}>
      <Typography>
        Scheduled no earlier than {displaySchedule(value)} ({scheduleTimezone}).
      </Typography>
      <Typography variant="body2" color="text.secondary">
        Expected pickup: {displaySchedule(nextAnnouncementPickup(value))} (
        {scheduleTimezone}). Pickups run at :15/:45 UTC; worker availability and
        provider restrictions may delay delivery.
      </Typography>
    </Stack>
  );
}
